import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, EffectFade, Zoom } from 'swiper/modules';
import { Heart, Share2, ShoppingCart, Plus, Minus, Package2, Shield, Truck, X, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { Product, SelectedVariation } from '@/types';
import { useStore } from '@/store';
import { useStoreInfo } from '@/lib/store';
import { formatPrice, getDomain, truncateText } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { LockNotice } from '@/components/site-lock/lock-notice';
import { calculateProductDiscount, calculateVariationDiscount } from '@/lib/discount';
import { DiscountBadge } from '@/components/ui/discount-badge';
import { AddedToCartModal } from '@/components/cart/added-to-cart-modal';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import 'swiper/css/zoom';

const features = [
  {
    icon: Truck,
    title: 'Fast Delivery',
    description: 'Get your order delivered quickly and securely'
  },
  {
    icon: Shield,
    title: 'Secure Payment',
    description: 'Your payment information is always protected'
  },
  {
    icon: Package2,
    title: 'Quality Guarantee',
    description: '100% satisfaction guaranteed on all products'
  }
];

export function ProductPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [suggestedProducts, setSuggestedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVariations, setSelectedVariations] = useState<SelectedVariation[]>([]);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showAddedToCartModal, setShowAddedToCartModal] = useState(false);
  const [showLockNotice, setShowLockNotice] = useState(false);
  const [addedProduct, setAddedProduct] = useState<any>(null);

  const { addToCart, toggleWishlist, wishlist } = useStore();
  const { storeInfo } = useStoreInfo();
  const isWishlisted = wishlist.includes(parseInt(id!));

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const domain = getDomain();
        const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/products.php?domain=${domain}`);
        if (!response.ok) throw new Error('Failed to fetch products');

        const data = await response.json();
        if (!data.status) throw new Error(data.message);

        const foundProduct = data.products.find((p: Product) => p.id === parseInt(id!));
        if (!foundProduct) {
          setError('Product not found');
          setLoading(false);
          return;
        }
        
        if (foundProduct.status === 3) {
          setError('Product is not available');
          setLoading(false);
          return;
        }
        
        if (foundProduct.totalStock <= 0) {
          setError('Product is out of stock');
          setLoading(false);
          return;
        }

        setProduct(foundProduct);

        const categoryProducts = data.products.filter((p: Product) =>
          p.category === foundProduct.category &&
          p.id !== foundProduct.id &&
          p.status === 1 &&
          p.totalStock > 0
        );

        const shuffled = categoryProducts.sort(() => 0.5 - Math.random());
        setSuggestedProducts(shuffled.slice(0, 4));

        if (foundProduct.variations.length > 0 && foundProduct.variations[0].variation !== 'Nil') {
          setSelectedVariations(foundProduct.variations.map(v => ({
            variation: v.variation,
            quantity: 0,
            id: parseInt(v.id)
          })));
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    fetchProducts();
  }, [id]);

  useEffect(() => {
    if (storeInfo?.site_lock === 1) {
      setShowLockNotice(true);
    }
  }, [storeInfo]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
            <div className="md:grid md:grid-cols-2 md:gap-8">
              <div className="animate-pulse">
                <div className="aspect-square bg-gray-200 dark:bg-gray-700" />
                <div className="p-4 flex gap-2">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                  ))}
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4" />
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mt-6" />
                <div className="space-y-2 mt-8">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                </div>
                <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded mt-8" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-12">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Product Not Found
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {error || 'The product you are looking for does not exist or has been removed.'}
            </p>
            <div className="flex justify-center gap-4">
              <Button
                variant="outline"
                onClick={() => window.history.back()}
              >
                Go Back
              </Button>
              <Link to="/products">
                <Button>
                  Browse Products
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleVariationQuantityChange = (variation: string, change: number) => {
    setSelectedVariations(prev => prev.map(v => {
      if (v.variation === variation) {
        const currentStock = product?.variations.find(pv => pv.variation === variation)?.v_qty || '0';
        const newQuantity = Math.max(0, Math.min(parseInt(currentStock), v.quantity + change));
        return { ...v, quantity: newQuantity };
      }
      return v;
    }));
  };

  const handleAddToCart = () => {
    if (storeInfo?.site_lock === 1) {
      setShowLockNotice(true);
      return;
    }

    if (!product) return;

    const isOutOfStock = product.variations[0].variation !== 'Nil'
      ? product.variations.every(v => parseInt(v.v_qty) === 0)
      : parseInt(product.totalStock.toString()) === 0;

    if (isOutOfStock) {
      toast.error('This product is out of stock');
      return;
    }

    if (product.variations[0].variation !== 'Nil') {
      const totalSelected = selectedVariations.reduce((sum, v) => sum + v.quantity, 0);
      if (totalSelected === 0) {
        toast.error('Please select at least one variation');
        return;
      }

      addToCart({
        ...product,
        quantity: 1,
        selectedVariations: selectedVariations
          .filter(v => v.quantity > 0)
          .map(v => ({
            ...v,
            v_id: v.v_id,
          })),
      });
    } else {
      if (quantity > parseInt(product.totalStock.toString())) {
        toast.error('Selected quantity exceeds available stock');
        return;
      }
      addToCart({
        ...product,
        quantity,
        selectedVariations: undefined,
      });
    }
    setShowAddedToCartModal(true);
    setAddedProduct({
      id: product.id,
      produt_name: product.produt_name,
      sellingprice: product.sellingprice,
      images: product.images,
    });
  };

  const handleWishlist = () => {
    if (!product) return;
    toggleWishlist(product.id);
    toast.success(isWishlisted ? 'Removed from wishlist' : 'Added to wishlist');
  };

  const handleShare = async () => {
    try {
      const shareData = {
        title: product?.produt_name,
        text: product?.description,
        url: window.location.href,
      };

      const shareLinks = {
        whatsapp: `https://wa.me/?text=${encodeURIComponent(`${shareData.title}\n\n${shareData.url}`)}`,
        telegram: `https://t.me/share/url?url=${encodeURIComponent(shareData.url)}&text=${encodeURIComponent(shareData.title)}`,
        twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(`${shareData.title}\n\n${shareData.url}`)}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareData.url)}`,
      };

      const shareDialog = document.createElement('div');
      shareDialog.className = 'fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50';
      shareDialog.innerHTML = `
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-sm w-full">
          <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Share Product</h3>
          <div class="grid grid-cols-2 gap-4">
            <a href="${shareLinks.whatsapp}" target="_blank" rel="noopener noreferrer" 
               class="flex items-center justify-center gap-2 p-3 rounded-lg bg-green-500 text-white hover:bg-green-600">
              WhatsApp
            </a>
            <a href="${shareLinks.telegram}" target="_blank" rel="noopener noreferrer"
               class="flex items-center justify-center gap-2 p-3 rounded-lg bg-brand-primary text-white hover:bg-brand-light">
              Telegram
            </a>
            <a href="${shareLinks.twitter}" target="_blank" rel="noopener noreferrer"
               class="flex items-center justify-center gap-2 p-3 rounded-lg bg-brand-primary text-white hover:bg-brand-light">
              Twitter
            </a>
            <a href="${shareLinks.facebook}" target="_blank" rel="noopener noreferrer"
               class="flex items-center justify-center gap-2 p-3 rounded-lg bg-brand-primary text-white hover:bg-brand-light">
              Facebook
            </a>
          </div>
          <button class="mt-4 w-full p-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
            Cancel
          </button>
        </div>
      `;

      document.body.appendChild(shareDialog);

      shareDialog.addEventListener('click', (e) => {
        if (e.target === shareDialog || (e.target as HTMLElement).tagName === 'BUTTON') {
          shareDialog.remove();
        }
      });

    } catch (err) {
      toast.error('Failed to share product');
    }
  };

  const availableVariations = product?.variations.filter(v => parseInt(v.v_qty) > 0) || [];

  return (
    <>
      <Helmet>
        <title>{product.produt_name} - {storeInfo?.site_name || 'Store'}</title>
        <meta name="description" content={product.description} />
        <meta property="og:title" content={product.produt_name} />
        <meta property="og:description" content={product.description} />
        <meta property="og:image" content={product.images[0]} />
        <meta property="og:url" content={window.location.href} />
      </Helmet>

      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
            <div className="md:grid md:grid-cols-2 md:gap-8">
              <div className="product-gallery">
                <Swiper
                  modules={[Navigation, Pagination, EffectFade, Zoom]}
                  navigation
                  pagination={{ clickable: true }}
                  effect="fade"
                  zoom={true}
                  className="aspect-square"
                  onSlideChange={(swiper) => setSelectedImage(swiper.activeIndex)}
                >
                  {product.images.map((image, index) => (
                    <SwiperSlide key={index}>
                      <div className="swiper-zoom-container">
                        <img
                          src={image}
                          alt={`${product.produt_name} - Image ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
                <div className="product-thumbnails">
                  <div className="product-thumbnails-container">
                    {product.images.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImage(index)}
                        className={`product-thumbnail ${
                          selectedImage === index
                            ? 'border-brand-primary dark:border-brand-light'
                            : 'border-transparent'
                        }`}
                      >
                        <img
                          src={image}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      {product.produt_name}
                    </h1>
                    {product.category && (
                      <Link
                        to={`/products?category=${encodeURIComponent(product.category)}`}
                        className="text-sm text-brand-primary dark:text-brand-light hover:underline"
                      >
                        {product.category}
                      </Link>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={handleWishlist}
                      className={`p-2 rounded-full ${isWishlisted
                        ? 'bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400'
                        : 'bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                        }`}
                    >
                      <Heart className="h-5 w-5" fill={isWishlisted ? 'currentColor' : 'none'} />
                    </motion.button>
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={handleShare}
                      className="p-2 rounded-full bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                    >
                      <Share2 className="h-5 w-5" />
                    </motion.button>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="flex items-baseline gap-4">
                    <span className="text-3xl font-bold text-gray-900 dark:text-white">
                      {formatPrice(product.sellingprice)}
                    </span>
                    {product.postType === "1" && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400">
                        Pre-Order
                      </span>
                    )}
                    {(() => {
                      const discountInfo = calculateProductDiscount(product, 1);
                      return discountInfo.hasDiscount && (
                        <DiscountBadge 
                          discountType={discountInfo.discountType}
                          discountAmount={discountInfo.discountAmount}
                        />
                      );
                    })()}
                  </div>
                  
                  {(() => {
                    const discountInfo = calculateProductDiscount(product, 1);
                    if (discountInfo.hasDiscount) {
                      const totalQuantity = product.variations[0].variation !== 'Nil' 
                        ? selectedVariations.reduce((sum, v) => sum + v.quantity, 0)
                        : quantity;
                      const currentDiscountInfo = product.variations[0].variation !== 'Nil'
                        ? calculateVariationDiscount(product, selectedVariations[0] || { variation: '', quantity: 0, v_id: '', variation_amount: '0' }, selectedVariations)
                        : calculateProductDiscount(product, totalQuantity);
                      
                      return (
                        <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-yellow-800 dark:text-yellow-400">
                              Bulk Discount Available
                            </span>
                            <span className="text-sm font-bold text-yellow-800 dark:text-yellow-400">
                              {discountInfo.discountType === 'percentage' 
                                ? `${discountInfo.discountAmount}% OFF`
                                : `${formatPrice(discountInfo.discountAmount)} OFF`
                              }
                            </span>
                          </div>
                          <p className="text-sm text-yellow-700 dark:text-yellow-300">
                            Buy {discountInfo.threshold} or more items to get the discount
                          </p>
                          {currentDiscountInfo.isEligible && (
                            <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                              <p className="text-sm font-medium text-green-800 dark:text-green-400">
                                🎉 Discount Applied! You save {formatPrice(currentDiscountInfo.discountValue * totalQuantity)}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>

                <div className="mt-8">
                  {product.variations[0].variation !== 'Nil' ? (
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        Select Variations
                      </h3>
                      <div className="space-y-4">
                        {availableVariations.map((variation, index) => (
                          <div key={index} className="flex items-center justify-between">
                            <div>
                              <span className="text-gray-700 dark:text-gray-300">
                                {variation.variation}
                              </span>
                              <div className="ml-2 flex items-center gap-2">
                                {(() => {
                                  const totalQuantity = selectedVariations.reduce((sum, v) => sum + v.quantity, 0);
                                  const currentVariation = selectedVariations.find(v => v.variation === variation.variation);
                                  if (currentVariation) {
                                    const discountInfo = calculateVariationDiscount(product, currentVariation, selectedVariations);
                                    if (discountInfo.isEligible) {
                                      return (
                                        <>
                                          <span className="text-sm text-gray-500 dark:text-gray-400 line-through">
                                            {formatPrice(variation.variation_amount)}
                                          </span>
                                          <span className="text-sm text-brand-primary dark:text-brand-light font-bold">
                                            {formatPrice(discountInfo.finalPrice)}
                                          </span>
                                        </>
                                      );
                                    }
                                  }
                                  return (
                                    <span className="text-sm text-brand-primary dark:text-brand-light">
                                      {formatPrice(variation.variation_amount)}
                                    </span>
                                  );
                                })()}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <motion.button
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handleVariationQuantityChange(variation.variation, -1)}
                                className="p-2 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                              >
                                <Minus className="h-4 w-4" />
                              </motion.button>
                              <span className="w-8 text-center text-gray-900 dark:text-white">
                                {selectedVariations.find(v => v.variation === variation.variation)?.quantity || 0}
                              </span>
                              <motion.button
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handleVariationQuantityChange(variation.variation, 1)}
                                className="p-2 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                              >
                                <Plus className="h-4 w-4" />
                              </motion.button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        Quantity
                      </h3>
                      <div className="flex items-center gap-4">
                        <motion.button
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setQuantity(Math.max(1, quantity - 1))}
                          className="p-2 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600"
                        >
                          <Minus className="h-4 w-4" />
                        </motion.button>
                        <input
                          type="number"
                          min="1"
                          max={product.totalStock}
                          value={quantity}
                          onChange={(e) => setQuantity(Math.max(1, Math.min(product.totalStock, parseInt(e.target.value) || 1)))}
                          className="w-20 text-center rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        />
                        {(() => {
                          const discountInfo = calculateProductDiscount(product, quantity);
                          if (discountInfo.isEligible) {
                            return (
                              <div className="ml-4 text-sm">
                                <span className="text-gray-500 dark:text-gray-400 line-through">
                                  {formatPrice(discountInfo.originalPrice)}
                                </span>
                                <span className="ml-2 text-brand-primary dark:text-brand-light font-bold">
                                  {formatPrice(discountInfo.finalPrice)} each
                                </span>
                              </div>
                            );
                          }
                          return null;
                        })()}
                        <motion.button
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setQuantity(Math.min(product.totalStock, quantity + 1))}
                          className="p-2 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600"
                        >
                          <Plus className="h-4 w-4" />
                        </motion.button>
                      </div>
                    </div>
                  )}
                </div>

                <motion.div
                  whileTap={{ scale: 0.98 }}
                  className="mt-8"
                >
                  <Button
                    onClick={handleAddToCart}
                    className="w-full h-12 text-lg font-medium"
                    disabled={product.variations[0].variation !== 'Nil'
                      ? availableVariations.length === 0
                      : parseInt(product.totalStock.toString()) === 0}
                  >
                    <ShoppingCart className="h-5 w-5 mr-2" />
                    {product.variations[0].variation !== 'Nil'
                      ? availableVariations.length === 0
                        ? 'Out of Stock'
                        : 'Add to Cart'
                       : parseInt(product.totalStock.toString()) === 0
                        ? 'Out of Stock'
                        : 'Add to Cart'}
                  </Button>
                </motion.div>

                <div className="mt-8 border-t dark:border-gray-700 pt-8">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Description
                  </h3>
                  <div className="prose prose-sm dark:prose-invert">
                    <p className="whitespace-pre-line text-gray-700 dark:text-gray-300">
                      {product.description}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {suggestedProducts.length > 0 && (
            <div className="mt-12">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                Suggested Products
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {suggestedProducts.map((suggestedProduct) => (
                  <div
                    key={suggestedProduct.id}
                    className="group bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all duration-300"
                  >
                    <Link to={`/product/${suggestedProduct.id}`}>
                      <div className="aspect-square relative overflow-hidden">
                        <img
                          src={suggestedProduct.images[0]}
                          alt={suggestedProduct.produt_name}
                          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-300"
                        />
                        {suggestedProduct.postType === "1" && (
                          <div className="absolute top-2 right-2 bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                            Pre-Order
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2 group-hover:text-brand-primary dark:group-hover:text-brand-light transition-colors">
                          {truncateText(suggestedProduct.produt_name, 50)}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                          {suggestedProduct.category || 'Uncategorized'}
                        </p>
                        <div className="flex justify-between items-center">
                          <span className="font-bold text-gray-900 dark:text-white">
                            {formatPrice(suggestedProduct.sellingprice)}
                          </span>
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {showAddedToCartModal && addedProduct && (
        <AddedToCartModal
          open={showAddedToCartModal}
          onClose={() => setShowAddedToCartModal(false)}
          product={addedProduct}
          quantity={quantity}
          selectedVariations={selectedVariations.filter(v => v.quantity > 0)}
          onContinueShopping={() => {
            setShowAddedToCartModal(false);
            navigate('/products');
          }}
          onGoToCart={() => {
            setShowAddedToCartModal(false);
            navigate('/cart');
          }}
        />
      )}
      <LockNotice
        open={showLockNotice}
        onClose={() => setShowLockNotice(false)}
      />
    </>
  );
}